import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Server, Users, ScrollText, Settings, LogOut, Moon, Sun, Cable } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/servers", label: "WHM Servers", icon: Server, testId: "nav-servers" },
  { to: "/mappings", label: "Mappings", icon: Users, testId: "nav-mappings" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testId: "nav-audit" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Cable className="h-4 w-4" strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-sm font-bold tracking-tight">SSO Bridge</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Halo · cPanel</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 p-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testId}
              className={({ isActive }) => cn(
                "group flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <n.icon className="h-4 w-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 rounded-md border border-border bg-muted/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Signed in</div>
            <div className="truncate text-xs font-medium" data-testid="sidebar-user-email">{user?.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" className="flex-1"
              onClick={toggle}
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? <Sun className="mr-1.5 h-4 w-4" /> : <Moon className="mr-1.5 h-4 w-4" />}
              {theme === "dark" ? "Light" : "Dark"}
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={onLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-heading font-semibold text-foreground" data-testid="header-title">Admin Console</span>
          </div>
          <div className="flex items-center gap-2 lg:hidden">
            <Button variant="ghost" size="sm" onClick={toggle} data-testid="theme-toggle-mobile-btn">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-mobile-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main className="mx-auto max-w-[1400px] p-6 lg:p-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
